/**
 * Entity & Guarantor Configuration Panel (SPRINT-15 F005)
 *
 * CRUD panel for managing which entities (accounts) and guarantors (contacts)
 * are included in a deal's financial analysis. Supports role assignment,
 * treatment configuration, and include/exclude toggles.
 *
 * States:
 * - Loading: Skeleton placeholders
 * - Empty: "No entities linked" with Add Entity / Add Guarantor buttons
 * - Loaded: Entity table + Guarantor table with actions
 * - Add Entity Dialog: Entity selector, role, notes
 * - Add Guarantor Dialog: Contact selector, treatment, income %, notes
 * - Edit Entity/Guarantor Dialog
 * - Delete AlertDialog confirmation
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Loader2, Users, Building2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { PeriodSelector } from './period-selector';

// ============================================================================
// TYPES
// ============================================================================

interface AnalysisEntity {
  id: string;
  dealId: string;
  accountId: string;
  accountName: string | null;
  entityRole: string;
  includeInAnalysis: boolean;
  displayOrder: number | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AnalysisGuarantor {
  id: string;
  dealId: string;
  contactId: string;
  contactName: string | null;
  treatment: string;
  includeInAnalysis: boolean;
  incomeContributionPercent: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Account {
  id: string;
  legalName: string;
  accountType?: string;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
}

interface EntityFormState {
  accountId: string;
  entityRole: string;
  notes: string;
  includeInAnalysis: boolean;
}

interface GuarantorFormState {
  contactId: string;
  treatment: string;
  incomeContributionPercent: string;
  notes: string;
  includeInAnalysis: boolean;
}

export interface EntityGuarantorConfigPanelProps {
  dealId: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const ENTITY_ROLE_OPTIONS = [
  { value: 'primary_borrower', label: 'Primary Borrower' },
  { value: 'co_borrower', label: 'Co-Borrower' },
  { value: 'guarantor_entity', label: 'Guarantor Entity' },
  { value: 'related_entity', label: 'Related Entity' },
];

const ENTITY_ROLE_LABELS: Record<string, string> = {
  primary_borrower: 'Primary Borrower',
  co_borrower: 'Co-Borrower',
  guarantor_entity: 'Guarantor Entity',
  related_entity: 'Related Entity',
};

const GUARANTOR_TREATMENT_OPTIONS = [
  { value: 'income_only', label: 'Income Only' },
  { value: 'debt_only', label: 'Debt Only' },
  { value: 'net_contribution', label: 'Net Contribution' },
  { value: 'full_inclusion', label: 'Full Inclusion' },
  { value: 'excluded', label: 'Excluded' },
];

const GUARANTOR_TREATMENT_LABELS: Record<string, string> = {
  income_only: 'Income Only',
  debt_only: 'Debt Only',
  net_contribution: 'Net Contribution',
  full_inclusion: 'Full Inclusion',
  excluded: 'Excluded',
};

const defaultEntityForm: EntityFormState = {
  accountId: '',
  entityRole: 'primary_borrower',
  notes: '',
  includeInAnalysis: true,
};

const defaultGuarantorForm: GuarantorFormState = {
  contactId: '',
  treatment: 'net_contribution',
  incomeContributionPercent: '100',
  notes: '',
  includeInAnalysis: true,
};

// ============================================================================
// COMPONENT
// ============================================================================

export const EntityGuarantorConfigPanel: React.FC<EntityGuarantorConfigPanelProps> = ({ dealId }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // ---------------------------------------------------------------------------
  // Modal state
  // ---------------------------------------------------------------------------
  const [entityModalMode, setEntityModalMode] = useState<'add' | 'edit' | null>(null);
  const [editingEntity, setEditingEntity] = useState<AnalysisEntity | null>(null);
  const [deleteEntity, setDeleteEntity] = useState<AnalysisEntity | null>(null);
  const [entityForm, setEntityForm] = useState<EntityFormState>(defaultEntityForm);

  const [guarantorModalMode, setGuarantorModalMode] = useState<'add' | 'edit' | null>(null);
  const [editingGuarantor, setEditingGuarantor] = useState<AnalysisGuarantor | null>(null);
  const [deleteGuarantor, setDeleteGuarantor] = useState<AnalysisGuarantor | null>(null);
  const [guarantorForm, setGuarantorForm] = useState<GuarantorFormState>(defaultGuarantorForm);

  // Track which entities have expanded period selectors
  const [expandedEntities, setExpandedEntities] = useState<Set<string>>(new Set());

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------
  const { data: deal } = useQuery<{ clientGroupId: string | null }>({
    queryKey: [`/api/crm/deals/${dealId}`],
    enabled: !!dealId,
  });
  const clientGroupId = deal?.clientGroupId;

  const { data: entities = [], isLoading: entitiesLoading } = useQuery<AnalysisEntity[]>({
    queryKey: [`/api/financial/deals/${dealId}/analysis-entities`],
    enabled: !!dealId,
  });

  const { data: guarantors = [], isLoading: guarantorsLoading } = useQuery<AnalysisGuarantor[]>({
    queryKey: [`/api/financial/deals/${dealId}/analysis-guarantors`],
    enabled: !!dealId,
  });

  // Fetch available accounts for the entity selector
  const { data: groupAccounts = [] } = useQuery<Account[]>({
    queryKey: ['/api/crm/accounts', { clientGroupId }],
    enabled: entityModalMode !== null && !!clientGroupId,
  });

  // Fetch available contacts for the guarantor selector
  const { data: groupContacts = [] } = useQuery<Contact[]>({
    queryKey: ['/api/crm/contacts', { clientGroupId }],
    enabled: guarantorModalMode !== null && !!clientGroupId,
  });
  // Disabled queries can still return cached data from an unscoped picker.
  const accounts = clientGroupId ? groupAccounts : [];
  const contacts = clientGroupId ? groupContacts : [];

  const isLoading = entitiesLoading || guarantorsLoading;

  // ---------------------------------------------------------------------------
  // Sync form state on edit
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (editingEntity && entityModalMode === 'edit') {
      setEntityForm({
        accountId: editingEntity.accountId,
        entityRole: editingEntity.entityRole,
        notes: editingEntity.notes || '',
        includeInAnalysis: editingEntity.includeInAnalysis,
      });
    }
  }, [editingEntity, entityModalMode]);

  useEffect(() => {
    if (editingGuarantor && guarantorModalMode === 'edit') {
      setGuarantorForm({
        contactId: editingGuarantor.contactId,
        treatment: editingGuarantor.treatment,
        incomeContributionPercent: editingGuarantor.incomeContributionPercent || '100',
        notes: editingGuarantor.notes || '',
        includeInAnalysis: editingGuarantor.includeInAnalysis,
      });
    }
  }, [editingGuarantor, guarantorModalMode]);

  // ---------------------------------------------------------------------------
  // Entity mutations
  // ---------------------------------------------------------------------------
  const createEntityMutation = useMutation({
    mutationFn: async (data: Omit<EntityFormState, 'includeInAnalysis'> & { includeInAnalysis: boolean }) => {
      const res = await apiRequest('POST', `/api/financial/deals/${dealId}/analysis-entities`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/financial/deals/${dealId}/analysis-entities`] });
      handleCloseEntityModal();
      toast({ title: 'Entity added', description: 'Entity has been linked to this deal' });
    },
    onError: (error: Error) => {
      if (error.message.includes('409')) {
        toast({ title: 'Already linked', description: 'Entity already linked to this deal', variant: 'destructive' });
      } else {
        toast({ title: 'Error', description: error.message || 'Failed to add entity', variant: 'destructive' });
      }
    },
  });

  const updateEntityMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<EntityFormState> }) => {
      const res = await apiRequest('PATCH', `/api/financial/deals/${dealId}/analysis-entities/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/financial/deals/${dealId}/analysis-entities`] });
      handleCloseEntityModal();
      toast({ title: 'Entity updated', description: 'Entity configuration has been updated' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to update entity', variant: 'destructive' });
    },
  });

  const deleteEntityMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest('DELETE', `/api/financial/deals/${dealId}/analysis-entities/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/financial/deals/${dealId}/analysis-entities`] });
      setDeleteEntity(null);
      toast({ title: 'Entity removed', description: 'Entity has been removed from the deal' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to delete entity', variant: 'destructive' });
    },
  });

  const toggleEntityIncludeMutation = useMutation({
    mutationFn: async ({ id, includeInAnalysis }: { id: string; includeInAnalysis: boolean }) => {
      const res = await apiRequest('PATCH', `/api/financial/deals/${dealId}/analysis-entities/${id}`, { includeInAnalysis });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/financial/deals/${dealId}/analysis-entities`] });
    },
  });

  // ---------------------------------------------------------------------------
  // Guarantor mutations
  // ---------------------------------------------------------------------------
  const createGuarantorMutation = useMutation({
    mutationFn: async (data: Omit<GuarantorFormState, 'includeInAnalysis'> & { includeInAnalysis: boolean }) => {
      const res = await apiRequest('POST', `/api/financial/deals/${dealId}/analysis-guarantors`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/financial/deals/${dealId}/analysis-guarantors`] });
      handleCloseGuarantorModal();
      toast({ title: 'Guarantor added', description: 'Guarantor has been linked to this deal' });
    },
    onError: (error: Error) => {
      if (error.message.includes('409')) {
        toast({ title: 'Already linked', description: 'Guarantor already linked to this deal', variant: 'destructive' });
      } else {
        toast({ title: 'Error', description: error.message || 'Failed to add guarantor', variant: 'destructive' });
      }
    },
  });

  const updateGuarantorMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<GuarantorFormState> }) => {
      const res = await apiRequest('PATCH', `/api/financial/deals/${dealId}/analysis-guarantors/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/financial/deals/${dealId}/analysis-guarantors`] });
      handleCloseGuarantorModal();
      toast({ title: 'Guarantor updated', description: 'Guarantor configuration has been updated' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to update guarantor', variant: 'destructive' });
    },
  });

  const deleteGuarantorMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest('DELETE', `/api/financial/deals/${dealId}/analysis-guarantors/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/financial/deals/${dealId}/analysis-guarantors`] });
      setDeleteGuarantor(null);
      toast({ title: 'Guarantor removed', description: 'Guarantor has been removed from the deal' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to delete guarantor', variant: 'destructive' });
    },
  });

  const toggleGuarantorIncludeMutation = useMutation({
    mutationFn: async ({ id, includeInAnalysis }: { id: string; includeInAnalysis: boolean }) => {
      const res = await apiRequest('PATCH', `/api/financial/deals/${dealId}/analysis-guarantors/${id}`, { includeInAnalysis });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/financial/deals/${dealId}/analysis-guarantors`] });
    },
  });

  // ---------------------------------------------------------------------------
  // Modal handlers — Entities
  // ---------------------------------------------------------------------------
  const handleOpenAddEntity = () => {
    setEditingEntity(null);
    setEntityForm(defaultEntityForm);
    setEntityModalMode('add');
  };

  const handleOpenEditEntity = (entity: AnalysisEntity) => {
    setEditingEntity(entity);
    setEntityModalMode('edit');
  };

  const handleCloseEntityModal = () => {
    setEntityModalMode(null);
    setEditingEntity(null);
    setEntityForm(defaultEntityForm);
  };

  const handleSaveEntity = async () => {
    if (!entityForm.accountId) return;

    if (entityModalMode === 'edit' && editingEntity) {
      await updateEntityMutation.mutateAsync({
        id: editingEntity.id,
        data: {
          entityRole: entityForm.entityRole,
          notes: entityForm.notes || undefined,
          includeInAnalysis: entityForm.includeInAnalysis,
        },
      });
    } else {
      await createEntityMutation.mutateAsync({
        accountId: entityForm.accountId,
        entityRole: entityForm.entityRole,
        notes: entityForm.notes || '',
        includeInAnalysis: entityForm.includeInAnalysis,
      });
    }
  };

  // ---------------------------------------------------------------------------
  // Modal handlers — Guarantors
  // ---------------------------------------------------------------------------
  const handleOpenAddGuarantor = () => {
    setEditingGuarantor(null);
    setGuarantorForm(defaultGuarantorForm);
    setGuarantorModalMode('add');
  };

  const handleOpenEditGuarantor = (guarantor: AnalysisGuarantor) => {
    setEditingGuarantor(guarantor);
    setGuarantorModalMode('edit');
  };

  const handleCloseGuarantorModal = () => {
    setGuarantorModalMode(null);
    setEditingGuarantor(null);
    setGuarantorForm(defaultGuarantorForm);
  };

  const handleSaveGuarantor = async () => {
    if (!guarantorForm.contactId) return;

    const incomePercent = parseFloat(guarantorForm.incomeContributionPercent);
    if (isNaN(incomePercent) || incomePercent < 0 || incomePercent > 100) return;

    if (guarantorModalMode === 'edit' && editingGuarantor) {
      await updateGuarantorMutation.mutateAsync({
        id: editingGuarantor.id,
        data: {
          treatment: guarantorForm.treatment,
          incomeContributionPercent: guarantorForm.incomeContributionPercent,
          notes: guarantorForm.notes || undefined,
          includeInAnalysis: guarantorForm.includeInAnalysis,
        },
      });
    } else {
      await createGuarantorMutation.mutateAsync({
        contactId: guarantorForm.contactId,
        treatment: guarantorForm.treatment,
        incomeContributionPercent: guarantorForm.incomeContributionPercent,
        notes: guarantorForm.notes || '',
        includeInAnalysis: guarantorForm.includeInAnalysis,
      });
    }
  };

  // ---------------------------------------------------------------------------
  // Period expansion toggle
  // ---------------------------------------------------------------------------
  const toggleEntityExpand = (entityId: string) => {
    setExpandedEntities((prev) => {
      const next = new Set(prev);
      if (next.has(entityId)) {
        next.delete(entityId);
      } else {
        next.add(entityId);
      }
      return next;
    });
  };

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------
  const isEntitySaving = createEntityMutation.isPending || updateEntityMutation.isPending;
  const isGuarantorSaving = createGuarantorMutation.isPending || updateGuarantorMutation.isPending;
  const hasNoData = entities.length === 0 && guarantors.length === 0;

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------
  if (isLoading) {
    return (
      <Card data-testid="entity-guarantor-config-panel">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-64" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      <Card data-testid="entity-guarantor-config-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">Entity & Guarantor Configuration</CardTitle>
            {!hasNoData && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenAddEntity}
                  data-testid="button-add-entity"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Entity
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenAddGuarantor}
                  data-testid="button-add-guarantor"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Guarantor
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {hasNoData ? (
            /* ===============================================================
               EMPTY STATE
               =============================================================== */
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Building2 className="h-8 w-8 text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground mb-2">
                No entities linked to this deal&apos;s financial analysis
              </p>
              <p className="text-xs text-muted-foreground mb-6">
                Add entities and guarantors to configure the deal&apos;s analysis scope
              </p>
              <div className="flex gap-2">
                <Button onClick={handleOpenAddEntity} data-testid="button-add-entity">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Entity
                </Button>
                <Button variant="outline" onClick={handleOpenAddGuarantor} data-testid="button-add-guarantor">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Guarantor
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* =============================================================
                  ENTITIES SECTION
                  ============================================================= */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-medium">
                    Entities ({entities.length})
                  </h3>
                </div>

                {entities.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-xs text-muted-foreground mb-2">No entities added</p>
                    <Button variant="outline" size="sm" onClick={handleOpenAddEntity} data-testid="button-add-entity">
                      <Plus className="h-4 w-4 mr-1" />
                      Add Entity
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto" data-testid="entity-table">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-2 font-medium text-muted-foreground">Entity Name</th>
                          <th className="text-left py-2 px-2 font-medium text-muted-foreground">Role</th>
                          <th className="text-center py-2 px-2 font-medium text-muted-foreground">Include</th>
                          <th className="text-right py-2 px-2 font-medium text-muted-foreground">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entities.map((entity) => (
                          <React.Fragment key={entity.id}>
                            <tr
                              className={`border-b ${!entity.includeInAnalysis ? 'opacity-50' : ''}`}
                              data-testid={`entity-row-${entity.id}`}
                            >
                              <td className="py-2 px-2">
                                <button
                                  className="text-left hover:underline cursor-pointer font-medium"
                                  onClick={() => toggleEntityExpand(entity.id)}
                                  data-testid={`entity-expand-${entity.id}`}
                                >
                                  {entity.accountName || 'Unknown Entity'}
                                </button>
                              </td>
                              <td className="py-2 px-2">
                                <Badge variant="outline">
                                  {ENTITY_ROLE_LABELS[entity.entityRole] || entity.entityRole}
                                </Badge>
                              </td>
                              <td className="py-2 px-2 text-center">
                                <Switch
                                  checked={entity.includeInAnalysis}
                                  onCheckedChange={(checked) =>
                                    toggleEntityIncludeMutation.mutate({ id: entity.id, includeInAnalysis: checked })
                                  }
                                  data-testid={`entity-include-${entity.id}`}
                                />
                              </td>
                              <td className="py-2 px-2 text-right">
                                <div className="flex justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleOpenEditEntity(entity)}
                                    aria-label="edit entity"
                                    data-testid={`entity-edit-${entity.id}`}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setDeleteEntity(entity)}
                                    aria-label="delete entity"
                                    data-testid={`entity-delete-${entity.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                            {/* Expanded period selector row */}
                            {expandedEntities.has(entity.id) && (
                              <tr>
                                <td colSpan={4} className="py-2 px-4 bg-muted/30">
                                  <PeriodSelector
                                    dealId={dealId}
                                    entityId={entity.id}
                                    accountId={entity.accountId}
                                  />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* =============================================================
                  GUARANTORS SECTION
                  ============================================================= */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-medium">
                    Guarantors ({guarantors.length})
                  </h3>
                </div>

                {guarantors.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-xs text-muted-foreground mb-2">No guarantors added</p>
                    <Button variant="outline" size="sm" onClick={handleOpenAddGuarantor} data-testid="button-add-guarantor">
                      <Plus className="h-4 w-4 mr-1" />
                      Add Guarantor
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto" data-testid="guarantor-table">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-2 font-medium text-muted-foreground">Name</th>
                          <th className="text-left py-2 px-2 font-medium text-muted-foreground">Treatment</th>
                          <th className="text-right py-2 px-2 font-medium text-muted-foreground">Income %</th>
                          <th className="text-center py-2 px-2 font-medium text-muted-foreground">Include</th>
                          <th className="text-right py-2 px-2 font-medium text-muted-foreground">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {guarantors.map((guarantor) => (
                          <tr
                            key={guarantor.id}
                            className={`border-b ${!guarantor.includeInAnalysis ? 'opacity-50' : ''}`}
                            data-testid={`guarantor-row-${guarantor.id}`}
                          >
                            <td className="py-2 px-2 font-medium">
                              {guarantor.contactName || 'Unknown Contact'}
                            </td>
                            <td className="py-2 px-2">
                              <Badge variant="outline">
                                {GUARANTOR_TREATMENT_LABELS[guarantor.treatment] || guarantor.treatment}
                              </Badge>
                            </td>
                            <td className="py-2 px-2 text-right">
                              {guarantor.incomeContributionPercent != null
                                ? `${guarantor.incomeContributionPercent}%`
                                : '-'}
                            </td>
                            <td className="py-2 px-2 text-center">
                              <Switch
                                checked={guarantor.includeInAnalysis}
                                onCheckedChange={(checked) =>
                                  toggleGuarantorIncludeMutation.mutate({ id: guarantor.id, includeInAnalysis: checked })
                                }
                                data-testid={`guarantor-include-${guarantor.id}`}
                              />
                            </td>
                            <td className="py-2 px-2 text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenEditGuarantor(guarantor)}
                                  aria-label="edit guarantor"
                                  data-testid={`guarantor-edit-${guarantor.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setDeleteGuarantor(guarantor)}
                                  aria-label="delete guarantor"
                                  data-testid={`guarantor-delete-${guarantor.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ===================================================================
          ADD / EDIT ENTITY DIALOG
          =================================================================== */}
      <Dialog open={entityModalMode !== null} onOpenChange={(open) => !open && handleCloseEntityModal()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {entityModalMode === 'edit' ? 'Edit Entity' : 'Add Entity'}
            </DialogTitle>
            <DialogDescription>
              {entityModalMode === 'edit'
                ? 'Update the entity configuration for this deal'
                : 'Link an entity (account) to this deal\'s financial analysis'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Entity selector (disabled when editing — cannot change linked account) */}
            <div className="space-y-2">
              <Label htmlFor="entityAccountId">Entity</Label>
              <select
                id="entityAccountId"
                value={entityForm.accountId}
                onChange={(e) => setEntityForm((prev) => ({ ...prev, accountId: e.target.value }))}
                disabled={entityModalMode === 'edit'}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="entity-account-select"
              >
                <option value="">Select an entity...</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.legalName}
                  </option>
                ))}
              </select>
            </div>

            {/* Role selector */}
            <div className="space-y-2">
              <Label htmlFor="entityRole">Role</Label>
              <select
                id="entityRole"
                value={entityForm.entityRole}
                onChange={(e) => setEntityForm((prev) => ({ ...prev, entityRole: e.target.value }))}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                data-testid="entity-role-select"
              >
                {ENTITY_ROLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="entityNotes">Notes</Label>
              <textarea
                id="entityNotes"
                value={entityForm.notes}
                onChange={(e) => setEntityForm((prev) => ({ ...prev, notes: e.target.value }))}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                placeholder="Additional notes about this entity's role..."
                data-testid="entity-notes-input"
              />
            </div>

            {/* Include in analysis */}
            <div className="flex items-center justify-between">
              <Label htmlFor="entityInclude">Include in Analysis</Label>
              <Switch
                id="entityInclude"
                checked={entityForm.includeInAnalysis}
                onCheckedChange={(checked) => setEntityForm((prev) => ({ ...prev, includeInAnalysis: checked }))}
                data-testid="entity-include-toggle"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseEntityModal} disabled={isEntitySaving}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEntity}
              disabled={isEntitySaving || !entityForm.accountId}
              data-testid="entity-save-button"
            >
              {isEntitySaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : entityModalMode === 'edit' ? (
                'Save'
              ) : (
                'Add Entity'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===================================================================
          ADD / EDIT GUARANTOR DIALOG
          =================================================================== */}
      <Dialog open={guarantorModalMode !== null} onOpenChange={(open) => !open && handleCloseGuarantorModal()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {guarantorModalMode === 'edit' ? 'Edit Guarantor' : 'Add Guarantor'}
            </DialogTitle>
            <DialogDescription>
              {guarantorModalMode === 'edit'
                ? 'Update the guarantor configuration for this deal'
                : 'Link a guarantor (contact) to this deal\'s financial analysis'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Contact selector (disabled when editing) */}
            <div className="space-y-2">
              <Label htmlFor="guarantorContactId">Guarantor</Label>
              <select
                id="guarantorContactId"
                value={guarantorForm.contactId}
                onChange={(e) => setGuarantorForm((prev) => ({ ...prev, contactId: e.target.value }))}
                disabled={guarantorModalMode === 'edit'}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="guarantor-contact-select"
              >
                <option value="">Select a contact...</option>
                {contacts.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {`${contact.firstName} ${contact.lastName}`.trim() || 'Unnamed Contact'}
                  </option>
                ))}
              </select>
            </div>

            {/* Treatment selector */}
            <div className="space-y-2">
              <Label htmlFor="guarantorTreatment">Treatment</Label>
              <select
                id="guarantorTreatment"
                value={guarantorForm.treatment}
                onChange={(e) => setGuarantorForm((prev) => ({ ...prev, treatment: e.target.value }))}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                data-testid="guarantor-treatment-select"
              >
                {GUARANTOR_TREATMENT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Income contribution percentage */}
            <div className="space-y-2">
              <Label htmlFor="incomeContributionPercent">Income Contribution (%)</Label>
              <Input
                id="incomeContributionPercent"
                type="number"
                min="0"
                max="100"
                step="1"
                value={guarantorForm.incomeContributionPercent}
                onChange={(e) => setGuarantorForm((prev) => ({ ...prev, incomeContributionPercent: e.target.value }))}
                placeholder="100"
                data-testid="guarantor-income-percent-input"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="guarantorNotes">Notes</Label>
              <textarea
                id="guarantorNotes"
                value={guarantorForm.notes}
                onChange={(e) => setGuarantorForm((prev) => ({ ...prev, notes: e.target.value }))}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                placeholder="Additional notes about this guarantor..."
                data-testid="guarantor-notes-input"
              />
            </div>

            {/* Include in analysis */}
            <div className="flex items-center justify-between">
              <Label htmlFor="guarantorInclude">Include in Analysis</Label>
              <Switch
                id="guarantorInclude"
                checked={guarantorForm.includeInAnalysis}
                onCheckedChange={(checked) => setGuarantorForm((prev) => ({ ...prev, includeInAnalysis: checked }))}
                data-testid="guarantor-include-toggle"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseGuarantorModal} disabled={isGuarantorSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveGuarantor}
              disabled={isGuarantorSaving || !guarantorForm.contactId}
              data-testid="guarantor-save-button"
            >
              {isGuarantorSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : guarantorModalMode === 'edit' ? (
                'Save'
              ) : (
                'Add Guarantor'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===================================================================
          DELETE ENTITY CONFIRMATION
          =================================================================== */}
      <AlertDialog open={!!deleteEntity} onOpenChange={(open) => !open && setDeleteEntity(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Entity</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {deleteEntity?.accountName || 'this entity'} from the deal analysis?
              This will also remove any period selections for this entity. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteEntity && deleteEntityMutation.mutate(deleteEntity.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteEntityMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                'Remove'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ===================================================================
          DELETE GUARANTOR CONFIRMATION
          =================================================================== */}
      <AlertDialog open={!!deleteGuarantor} onOpenChange={(open) => !open && setDeleteGuarantor(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Guarantor</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {deleteGuarantor?.contactName || 'this guarantor'} from the deal analysis?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteGuarantor && deleteGuarantorMutation.mutate(deleteGuarantor.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteGuarantorMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                'Remove'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EntityGuarantorConfigPanel;
